import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import utils from "utils/utils";
import Loader from "components/V2/Loader/Loader";
import IconV2 from "components/V2/Icons/IconV2";
import BadgeV2 from "components/V2/Badges/BadgeV2";

import { IExternalResume } from "types/ExternalResume";
import ExternalResumesApi from "network/external/external_resumes";
import UserAvatarLogo from "views/application/talent/components/UserAvatarLogo";
import formatDate from "helpers/date";

const ExternalResumeDetail = () => {
  const { email } = useParams();
  const navigate = useNavigate();
  const [resume, setResume] = useState<IExternalResume>();

  const onCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Copied!")
  }

  const fetchData = async () => {
    if (!email) return;

    try {
      const r = await ExternalResumesApi.getOne({ email });
      if (!r) throw new Error("resume not found");
      setResume(r);
    } catch (e) {
      toast.error("Error while fetching talent data");
      console.error(e);
    }
  }
  useEffect(() => { fetchData() }, []);


  if (!resume) return <Loader />

  const { first_name, last_name, years_experience, location, roles, skills, phone, last_job_date, open_to } = resume;

  return (
    <div className="w-full h-screen overflow-auto">
      {/* Profile data */}
      <div>
        {/* Go back row */}
        <div className="flex pl-[37px] pr-[25px] h-[53px] w-full items-center">
          <div className="flex items-center hover:cursor-pointer" onClick={() => navigate(`/external_resumes/overview`)}>
            <IconV2 iconType="BACK-ARROW" iconClassName="w-[25px] h-[25px]" />
            <span className="text-[14px] font-inter font-semibold ml-[15px]">Back</span>
          </div>
        </div>

        {/* First row */}
        <div className="flex items-end h-fit py-[11px] pl-[37px] pr-[25px] border-solid border-y-[1px] border-dark-gray">
          <div className="flex items-center gap-x-[18px]">
            <UserAvatarLogo user={{} as any} className="!w-[70px] !h-[70px]" />
            <div className="flex flex-col">
              <span className="font-poppins font-semibold text-[16px]">{`${first_name || ""} ${last_name || ""}`}</span>
              <span className="font-inter font-semibold text-[14px]">{roles?.[0] || "N/A"}</span>
            </div>
          </div>
          <div className="flex flex-1 items-end justify-between ml-[60px] mb-[12px]">
            <div className="flex gap-x-[48px]">
              <div className="flex gap-[12px]">
                <IconV2 iconType="EDUCATION" iconClassName="w-[17px]" />
                <span className="font-inter font-semibold text-[12px] text-dark-gray">{years_experience} Years</span>
              </div>
              <div className="flex gap-[12px]">
                <IconV2 iconType="LOCATION" iconClassName="w-[17px]" />
                <span className="font-inter font-semibold text-[12px] text-dark-gray">{location || "N/A"}</span>
              </div>
              <div className="flex gap-[12px]">
                <IconV2 iconType="CLOCK" iconClassName="w-[17px]" />
                <span className="font-inter font-semibold text-[12px] text-dark-gray">{last_job_date ? formatDate.mmyyyy(last_job_date) : "N/A"}</span>
              </div>
            </div>
            <div className="flex gap-[13px]">
              <a href={resume?.resume_presigned} target="_blank" className="w-[32px] h-[32px] bg-[#F4F4F4] flex items-center rounded-lg">
                <IconV2 iconType="EYE" iconClassName="mx-auto w-[20px]" />
              </a>
              <div onClick={onCopy} className="w-[32px] h-[32px] bg-[#F4F4F4] flex items-center rounded-lg hover:cursor-pointer">
                <IconV2 iconType="CLIPBOARD" iconClassName="w-[20px] mx-auto" />
              </div>
            </div>
          </div>
        </div>

        {/* Second row */}
        <div className="flex pt-[15px] pb-[13px] px-[37px] justify-between border-solid border-b-[1px] border-dark-gray">
          <div className="flex gap-[28px]">
            <span className="font-inter font-semibold text-[14px]">Contact</span>
            <div className="flex gap-[10px]">
              <IconV2 iconType="EMAIL" />
              <span className="font-inter text-[14px]">{email}</span>
            </div>
            <div className="flex gap-[10px]">
              <IconV2 iconType="PHONE" />
              <span className="font-inter text-[14px]">{phone || "N/A"}</span>
            </div>

            {open_to && <div className="flex gap-[10px]">
              <IconV2 iconType="LOCATION" />
              <span className="font-inter text-[14px]">{open_to || "N/A"}</span>
            </div>}
          </div>
        </div>

        {/* Third row */}
        <div className="relative flex pt-[29px] px-[37px] pb-[73px]">
          <div className="flex flex-col gap-y-[22px] w-[60%]">
            <div className="flex gap-x-[12px]">
              <span className="font-inter text-[14px] min-w-[120px]">skills</span>
              <div className="flex flex-wrap gap-[8px]">
                {
                  skills?.map(skill =>
                    <BadgeV2 key={skill} starInBadge color="pink">{skill}</BadgeV2>
                  )
                }
              </div>
            </div>

            <div className="flex gap-x-[12px]">
              <span className="font-inter text-[14px] min-w-[120px]">Roles</span>
              <div className="flex flex-wrap gap-[8px]">
                {roles?.length ? roles.map(r =>
                  <BadgeV2 key={r} color="orange">{utils.capitalizeFirstLetter(r)}</BadgeV2>
                ) :
                  <span className="font-inter text-[14px] font-semibold">N/A</span>
                }
              </div>
            </div>

            <div className="flex gap-x-[12px]">
              <span className="font-inter text-[14px] min-w-[120px]">City, country</span>
              <span className="font-inter text-[14px] font-semibold">{location || "N/A"}</span>
            </div>


          </div>



        </div>
      </div>

    </div>
  );
}

export default ExternalResumeDetail;